import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import enTranslations from '../translations/en.js';
import { getLanguageFromPath, localizePath } from '../utils/localeRoutes';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeLanguage = getLanguageFromPath(location.pathname);

  const [language, setLanguageState] = useState(routeLanguage);

  const [translations, setTranslations] = useState(() => {
    // Initialize with English translations to prevent flash
    return routeLanguage === 'en' ? enTranslations : {};
  });
  const [isLoading, setIsLoading] = useState(() => {
    // Only show loading if we need to load non-English translations
    return routeLanguage !== 'en';
  });

  useEffect(() => {
    setLanguageState(routeLanguage);
  }, [routeLanguage]);

  useEffect(() => {
    // Load translations for the current language
    loadTranslations(language);
    
    // Save language preference
    localStorage.setItem('preferred-language', language);
    
    // Set document direction and language
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    
    // Add language class to body for CSS targeting
    document.body.className = document.body.className.replace(/lang-\w+/g, '');
    document.body.classList.add(`lang-${language}`);
  }, [language]);

  const loadTranslations = async (lang) => {
    // If it's English, we already have it loaded
    if (lang === 'en') {
      setTranslations(enTranslations);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const translationModule = await import(`../translations/${lang}.js`);
      setTranslations(translationModule.default);
    } catch (error) {
      console.error(`Failed to load translations for ${lang}:`, error);
      // Fallback to English if translation fails
      setTranslations(enTranslations);
    } finally {
      setIsLoading(false);
    }
  };

  const setLanguage = (nextLanguage) => {
    const languageToUse = nextLanguage === 'ar' ? 'ar' : 'en';
    const destination = localizePath(
      `${location.pathname}${location.search}${location.hash}`,
      languageToUse
    );

    navigate(destination);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  const t = (key, fallback = key) => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    
    return value || fallback;
  };

  const isRTL = language === 'ar';

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      toggleLanguage,
      t,
      isRTL,
      localizedPath: (path) => localizePath(path, language),
      translations,
      isLoading
    }}>
      {children}
    </LanguageContext.Provider>
  );
};
